import * as THREE from "three";
import { Context } from "../core/context";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";

import {
  CommonColor,
  DimensionsWireCountModifyAcceptParams,
  DimensionsWireCountModifyReturnData,
  Linearity,
  SampleLayerType,
} from "@/core/Parameters";
import { TextModel } from "@/core/TextModel";
import { DbLine } from "@/core/DbLine";
import { DbMesh } from "@/core/DbMesh";
import { ULabel } from "@/ui/ULabel";

class DimensionsWireCountModify implements IModifyOperation {
  private m_context: Context;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_locationObject: THREE.Line;
  private m_tempObjectGroup: THREE.Group = new THREE.Group();
  private m_returnData: DimensionsWireCountModifyReturnData = {
    signMatrixs: [],
    textPoints: [],
  };
  private m_points: THREE.Vector3[] = [];
  private m_textModel: TextModel;
  private m_textLabel: ULabel;
  private m_acceptParams: DimensionsWireCountModifyAcceptParams;

  constructor(
    context: Context,
    acceptParams: DimensionsWireCountModifyAcceptParams
  ) {
    this.m_context = context;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.m_context.SceneManager.AuxModel.add(this.m_tempObjectGroup);
    this.m_textModel = new TextModel(this.m_context);
    this.m_textLabel = this.m_context.ModifyManager.TextLabel;
    this.m_textLabel.Visibility = true;
    this.m_acceptParams = acceptParams;
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point;
    this.m_locationObject.position.copy(this.m_curPoint);
    this.updatedTextLabel();
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    let { obejct } = point;
    if (obejct?.length) {
      let obj = obejct[0];
      if (obj instanceof DbLine || obj instanceof DbMesh) {
        let layerName = this.m_context.ModelManager.GetLayerById(
          obj.layerId
        )?.Name;
        if (layerName) {
          let sampleData =
            this.m_context.ModelManager.GetSampleDataByLayerName(layerName);
          if (
            sampleData &&
            this.m_acceptParams.accordLayerNames.includes(
              sampleData?.LayerName
            ) &&
            obj.points?.length
          ) {
            let zAxis = new THREE.Vector3(0, 0, 1);
            let xAxis = new THREE.Vector3(1, 0, 0);
            this.m_points.push(point.point.clone());
            let lineStartP = new THREE.Vector3(
              obj.points[0],
              obj.points[1],
              obj.points[2]
            );
            let lineEndP = new THREE.Vector3(
              obj.points.at(-3),
              obj.points.at(-2),
              obj.points.at(-1)
            );
            let dir = lineEndP.clone().sub(lineStartP).normalize();
            let rotateDir1 = dir.clone().applyAxisAngle(zAxis, Math.PI / 4);
            let rotateDir2 = dir
              .clone()
              .setLength(0.25)
              .applyAxisAngle(zAxis, Math.PI / 2);
            let angle = rotateDir1.angleTo(xAxis);
            if (rotateDir1.clone().cross(xAxis).z > 0) {
              angle = Math.PI * 2 - angle;
            }
            let matrix4 = new THREE.Matrix4();
            matrix4.compose(
              point.point.clone(),
              new THREE.Quaternion().setFromAxisAngle(zAxis, angle),
              new THREE.Vector3(1, 1, 1)
            );
            this.createTempLine(matrix4);
            let textPosition = rotateDir2.clone().add(point.point);
            let textModel = this.m_textModel.CreateText({
              text: this.m_acceptParams.wireCount,
            });
            textModel.position.copy(textPosition);
            this.m_tempObjectGroup.add(textModel);
            this.m_returnData.signMatrixs.push(matrix4);
            this.m_returnData.textPoints.push(textPosition);
            this.m_acceptParams.callbackUnderway(point.point.clone());
          }
        }
      }
    }
  }
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.ctrlKey && (e.key == "z"||e.key=="Z")) {
      this.revocationData();
    }
    if (e.key == "Escape") {
      this.m_context.ModifyManager.ModifyFinish(ModifyOperateState.cancel);
    }
  }
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  private createTempLine(matrix: THREE.Matrix4) {
    let geo = new THREE.BufferGeometry();
    geo.setFromPoints([
      new THREE.Vector3(-0.2, 0, 0),
      new THREE.Vector3(0.2, 0, 0),
    ]);
    let mat = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let line = new THREE.LineSegments(geo, mat);
    line.frustumCulled = false;
    line.applyMatrix4(matrix);
    this.m_tempObjectGroup.add(line);
  }

  private updatedTextLabel() {
    let textLabelPoint = this.m_curPoint.clone();
    textLabelPoint.x += 60 * this.m_context.SelectionManager.Scale;
    textLabelPoint.y -= 10 * this.m_context.SelectionManager.Scale;
    this.m_textLabel.SetVaule(
      "按Ctrl+Z键将撤销回退到上一点,按Esc键将退出操作",
      textLabelPoint,
      new THREE.Vector3(1, 0, 0)
    );
  }

  private revocationData() {
    this.m_points.pop();
    this.m_returnData.signMatrixs.pop();
    this.m_returnData.textPoints.pop();
    let textModel = this.m_tempObjectGroup.children.pop();
    let signLine = this.m_tempObjectGroup.children.pop();
    if (textModel && signLine) {
      this.m_context.ToolsManager.ObjectsClear([textModel, signLine]);
    }
    this.m_context.ViewManager.Render();
  }

  private clear() {
    this.m_context.ToolsManager.ObjectsClear([this.m_locationObject]);
    this.m_context.ToolsManager.GroupClear(this.m_tempObjectGroup);
  }
}

export { DimensionsWireCountModify };
